import { useStrings } from "@1js/localization-components";
import { Column, Grid, Row } from "@1js/pl-card-components";
import { useMsal } from "@azure/msal-react";
import { Button } from "@fluentui/react-components";
import * as React from "react";
import { SectionTitle } from "../../components/SectionTitle/SectionTitle.web";
import { useFlightContext } from "../../hooks/useFlightContext";
import useMedia from "../../hooks/useMedia/useMedia";
import { fetchAllPrompts } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import {
  PromptsHeading,
  ViewMorePrompts,
} from "../home/HomePageContent.web.strings";
import { MetaOSAppContext } from "../metaos/MetaOSAppContext";
import { AllPromptsLibrary } from "../prompts/allPromptsLibrary/AllPromptsLibrary.web";
import usePrompts from "../prompts/usePrompts.web";
import { useStyles } from "./PromptGridSection.web.styles";

export type PromptGridSectionProps = {
  locale: string;
  serviceHost: string;
};

const { origin, pathname } = window.location;
const allLink = `${origin}${pathname}/all`;

export const PromptGridSection: React.FC<PromptGridSectionProps> = ({
  serviceHost,
  locale,
}) => {
  const telemetry = useTelemetry();
  const localizations = useStrings({
    ViewMorePrompts,
    PromptsHeading,
  });
  const styles = useStyles();
  const { state, dispatch } = usePrompts();
  const { isFlightActive } = useFlightContext();
  const isDesktop = useMedia({ minWidth: "954px" });
  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const fetchPromptsAsync = React.useCallback(async () => {
    if (!state.init) {
      try {
        const startTimer = Date.now();
        dispatch({ type: "REQUEST_ALL_PROMPTS_INIT" });
        const [prompts, is3SServiceEnabled] = await fetchAllPrompts(
          serviceHost,
          locale,
          telemetry.sendApiActionEvent,
          isFlightActive("is3SServiceEnabled"),
          instance
        );
        dispatch({
          type: "REQUEST_ALL_PROMPTS_SUCCEED",
          payload: { prompts: prompts.slice(0, 8) },
        });
        telemetry.sendResultsRenderedEvent(
          Date.now() - startTimer,
          is3SServiceEnabled,
          prompts,
          instance
        );
      } catch (error) {
        console.error("Error fetching more prompts:", error);
        dispatch({ type: "REQUEST_ALL_PROMPTS_FAILED" });
      }
    }
    // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.init]);

  React.useEffect(() => {
    fetchPromptsAsync();
  }, [fetchPromptsAsync]);

  return (
    <Grid goBold={isFlightActive("isGoBoldPromptCardEnabled")}>
      <Row>
        <Column size={12}>
          <SectionTitle title={localizations.PromptsHeading()} />
        </Column>
      </Row>
      <Row>
        <Column size={12}>
          <AllPromptsLibrary
            status={state.status}
            isShowFilters={false}
            isShowHasMore={false}
            prompts={state.allPrompts}
            serviceHost={serviceHost}
            locale={locale}
            skeletonCount={8}
            className={styles.allPrompts}
            enableCarousel={!isDesktop}
            isRedirectToTabs={true}
          />
        </Column>
      </Row>
      <Row className={styles.viewMoreBtnWrapper}>
        <Column size="auto">
          <Button
            className={styles.viewMoreBtn}
            size="large"
            appearance="subtle"
            as="a"
            href={allLink}
            data-testid="view-all-prompts"
          >
            {localizations.ViewMorePrompts()}
          </Button>
        </Column>
      </Row>
    </Grid>
  );
};
